import { defineQuery } from "@rocicorp/zero";
import { z } from "zod";
import type {} from "../zero-context";
import { zql } from "../schema";
import { workspaceFullMemberGuard } from "./access-filters";

export const listsQueries = {
  byWorkspace: defineQuery(z.string(), ({ args: workspaceId, ctx: { sub } }) =>
    zql.lists
      .where("workspace_id", workspaceId)
      .whereExists("workspace", workspaceFullMemberGuard(sub))
      .orderBy("created_at", "asc")
      .related("creator"),
  ),

  byId: defineQuery(z.string(), ({ args: id, ctx: { sub } }) =>
    zql.lists
      .where("id", id)
      .whereExists("workspace", workspaceFullMemberGuard(sub))
      .related("creator")
      .one(),
  ),

  // Phase 5 — single composite for ListView. Returns the list metadata +
  // creator + ordered items (each with its own creator). Replaces the
  // 2-way fan-out in `ListView` (`lists.byId` + `list_items.byList`).
  //
  // Access: parent-level `workspaceFullMemberGuard(sub)` requires the
  // caller to be a FULL_MEMBER of the list's workspace. The same guard
  // the prior `lists.byId` and `list_items.byList` queries enforced —
  // the items inherit their parent list's gate transitively (no list →
  // no items), so no separate filter is needed inside `.related("items")`.
  // Items preserve `position`-ASC ordering and `.related("creator")` to
  // match `list_items.byList`'s semantics.
  byIdComposite: defineQuery(z.string(), ({ args: id, ctx: { sub } }) =>
    zql.lists
      .where("id", id)
      .whereExists("workspace", workspaceFullMemberGuard(sub))
      .related("creator")
      .related("items", (q) => q.orderBy("position", "asc").related("creator"))
      .one(),
  ),
};

export const listItemsQueries = {
  byList: defineQuery(z.string(), ({ args: listId, ctx: { sub } }) =>
    zql.list_items
      .where("list_id", listId)
      .whereExists("list", (lq) =>
        lq.whereExists("workspace", workspaceFullMemberGuard(sub)),
      )
      .orderBy("position", "asc")
      .related("creator"),
  ),

  byId: defineQuery(z.string(), ({ args: id, ctx: { sub } }) =>
    zql.list_items
      .where("id", id)
      .whereExists("list", (lq) =>
        lq.whereExists("workspace", workspaceFullMemberGuard(sub)),
      )
      .related("creator")
      .related("list")
      .related("comments", (q) =>
        q.orderBy("created_at", "asc").related("creator"),
      )
      .one(),
  ),
};

export const listItemCommentsQueries = {
  byItem: defineQuery(z.string(), ({ args: listItemId, ctx: { sub } }) =>
    zql.list_item_comments
      .where("list_item_id", listItemId)
      .whereExists("item", (iq) =>
        iq.whereExists("list", (lq) =>
          lq.whereExists("workspace", workspaceFullMemberGuard(sub)),
        ),
      )
      .orderBy("created_at", "asc")
      .related("creator"),
  ),
};
